import dayjs from 'dayjs'
import type { LottieRefCurrentProps } from 'lottie-react'
import React from 'react'

import { breakpoints } from '../constants/breakPoints'
import {
  EducationResourcesEbooksIdReferences,
  EducationResourcesGuidesSlugReferences,
  EducationResourcesIdReferences,
} from '../constants/contentfulReferences'
import { Event } from '../interfaces/events'
import { LearnArticle } from '../interfaces/learn'
// Videos for Intro (Hero) & Objectives (Feature LP Template)
import homepageVideoMP4 from '../video/homepage.mp4'
import homepageVideoWebm from '../video/homepage.webm'
import insightsVideoMP4 from '../video/product/insights.mp4'
import insightsVideoWebm from '../video/product/insights.webm'
import okrFrameworkVideoMP4 from '../video/product/objectives/okr-framework.mp4'
import okrFrameworkVideoWebm from '../video/product/objectives/okr-framework.webm'
import okrVideoMP4 from '../video/product/objectives/okr-hero.mp4'
import okrVideoWebm from '../video/product/objectives/okr-hero.webm'
import okrLinkingVideoMP4 from '../video/product/objectives/okr-linking.mp4'
import okrLinkingVideoWebm from '../video/product/objectives/okr-linking.webm'
import okrOutcomeVideoMP4 from '../video/product/objectives/okr-outcome.mp4'
import okrOutcomeVideoWebm from '../video/product/objectives/okr-outcome.webm'
import okrProgressVideoMP4 from '../video/product/objectives/okr-progress.mp4'
import okrProgressVideoWebm from '../video/product/objectives/okr-progress.webm'
import okrTransparencyVideoMP4 from '../video/product/objectives/okr-transparency.mp4'
import okrTransparencyVideoWebm from '../video/product/objectives/okr-transparency.webm'
import portalVideoMP4 from '../video/product/portal.mp4'
import portalVideoWebm from '../video/product/portal.webm'
import prioritizationVideoMP4 from '../video/product/prioritization.mp4'
import prioritizationVideoWebm from '../video/product/prioritization.webm'
import roadmapsVideoMP4 from '../video/product/roadmaps.mp4'
import roadmapsVideoWebm from '../video/product/roadmaps.webm'

export const hexToHSL = (hex: string) => {
  // Convert hex to RGB first
  let r: any = 0
  let g: any = 0
  let b: any = 0
  if (hex.length === 4) {
    r = '0x' + hex[1] + hex[1]
    g = '0x' + hex[2] + hex[2]
    b = '0x' + hex[3] + hex[3]
  } else if (hex.length === 7) {
    r = '0x' + hex[1] + hex[2]
    g = '0x' + hex[3] + hex[4]
    b = '0x' + hex[5] + hex[6]
  }

  // then to HSL
  r /= 255
  g /= 255
  b /= 255
  const cmin = Math.min(r, g, b)
  const cmax = Math.max(r, g, b)
  const delta = cmax - cmin
  let h = 0
  let s = 0
  let l = 0

  if (delta === 0) {
    h = 0
  } else if (cmax === r) {
    h = ((g - b) / delta) % 6
  } else if (cmax === g) {
    h = (b - r) / delta + 2
  } else {
    h = (r - g) / delta + 4
  }

  h = Math.round(h * 60)

  if (h < 0) {
    h += 360
  }

  l = (cmax + cmin) / 2
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))
  s = +(s * 100).toFixed(1)
  l = +(l * 100).toFixed(1)

  return { h, s, l }
}

export function getRegion(): string | undefined {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return timeZone.split('/', 2)[0]
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err)
    return undefined
  }
}

export const getEducationResourceUrl = (id: string, slug: string) => {
  switch (id) {
    // educationCategory id is used here for:
    // eBooks
    case EducationResourcesIdReferences.eBooks:
      return `/resources/ebooks/${slug}/`
    // 5-minute guides
    case EducationResourcesIdReferences.fiveMinGuides:
      return `/resources/5-min-guides/${slug}/`
    // Checklists
    case EducationResourcesIdReferences.checklists:
      return `/resources/checklists/${slug}/`
    // Reports - DISABLED FOR NOW (2025-02-13)
    // case EducationResourcesIdReferences.reports:
    //   return `/resources/reports/${slug}/`
    // In-depth guides
    case EducationResourcesIdReferences.guides:
      return `/resources/guides/${slug}/`
    default:
      return `/resources/${slug}/`
  }
}

export const getRelatedGuideUrlForEbook = (id: string) => {
  switch (id) {
    case EducationResourcesEbooksIdReferences.okrsHighPerformingTeams:
    case EducationResourcesEbooksIdReferences.okrsRoundtable:
      return EducationResourcesGuidesSlugReferences.objectivesOKRs
    case EducationResourcesEbooksIdReferences.impactOfAi:
      return EducationResourcesGuidesSlugReferences.impactOfAi
    case EducationResourcesEbooksIdReferences.productFirst:
    case EducationResourcesEbooksIdReferences.productStrategy:
      return EducationResourcesGuidesSlugReferences.productStrategy
    case EducationResourcesEbooksIdReferences.productManagement:
    case EducationResourcesEbooksIdReferences.productManagementOutcomeBased:
      return EducationResourcesGuidesSlugReferences.productManagement
    case EducationResourcesEbooksIdReferences.roadmaps:
      return EducationResourcesGuidesSlugReferences.roadmaps
    case EducationResourcesEbooksIdReferences.customerFeedback:
      return EducationResourcesGuidesSlugReferences.customerFeedback
    case EducationResourcesEbooksIdReferences.productOps:
      return EducationResourcesGuidesSlugReferences.productOps
    case EducationResourcesEbooksIdReferences.internalPm:
      return EducationResourcesGuidesSlugReferences.internalPm
    case EducationResourcesEbooksIdReferences.featureFactory:
    case EducationResourcesEbooksIdReferences.featureFactoryRoundtable:
      return EducationResourcesGuidesSlugReferences.featureFactory
    case EducationResourcesEbooksIdReferences.productManagerJob:
      return EducationResourcesGuidesSlugReferences.productManagerJob
    case EducationResourcesEbooksIdReferences.agile:
      return EducationResourcesGuidesSlugReferences.agile
    case EducationResourcesEbooksIdReferences.prioritization:
      return EducationResourcesGuidesSlugReferences.prioritization
    default:
      return EducationResourcesGuidesSlugReferences.productManagement
  }
}

export const getEducationConfirmationUrl = (id: string, slug: string) =>
  `${getEducationResourceUrl(id, slug)}thank-you/`

export const sortLearnArticlesByDate = (firstArticle: LearnArticle, secondArticle: LearnArticle) =>
  dayjs(secondArticle.createdAt).toDate().getTime() - dayjs(firstArticle.createdAt).toDate().getTime()

export const isMobile = (breakpoint?: number) => {
  return (
    typeof window !== `undefined` && window.matchMedia(`(max-width: ${breakpoint || breakpoints.medium}px)`).matches
  )
}

export const isDesktop = (breakpoint?: number) => {
  // if server rendering assume desktop layout
  if (typeof window === `undefined`) {
    return true
  }
  return window.matchMedia(`(min-width: ${breakpoint || breakpoints.large}px)`).matches
}

export const getPressReleasesColor = (index: number) => {
  switch (index % 4) {
    case 0:
      return 'blue'
    case 1:
      return 'green'
    case 2:
      return 'purple'
    case 3:
      return 'peach'
    default:
      return 'blue'
  }
}

export const getSliderCardNumberColor = (index: number) => {
  switch (index % 3) {
    case 0:
      return '#318EFF'
    case 1:
      return '#0DCEBD'
    case 2:
      return '#BD97FF'
    default:
      return '#318EFF'
  }
}

export const getSliderCardBackgroundColor = (index: number) => {
  switch (index % 3) {
    case 0:
      return '#DDF4FF'
    case 1:
      return '#C5F7EF'
    case 2:
      return '#EAE0FF'
    default:
      return '#DDF4FF'
  }
}

export const getHeroVideo = (slug: string) => {
  switch (slug) {
    case 'roadmaps':
      return { MP4: roadmapsVideoMP4, Webm: roadmapsVideoWebm }
    case 'prioritization':
      return { MP4: prioritizationVideoMP4, Webm: prioritizationVideoWebm }
    case 'insights':
      return { MP4: insightsVideoMP4, Webm: insightsVideoWebm }
    case 'portal':
      return { MP4: portalVideoMP4, Webm: portalVideoWebm }
    case 'objectives-okrs':
      return { MP4: okrVideoMP4, Webm: okrVideoWebm }
    default:
      return { MP4: homepageVideoMP4, Webm: homepageVideoWebm }
  }
}

export type ObjectivesVideoFileName =
  | 'okr-framework'
  | 'okr-linking'
  | 'okr-outcome'
  | 'okr-progress'
  | 'okr-transparency'
export const getOkrVideo = (filename: ObjectivesVideoFileName) => {
  switch (filename) {
    case 'okr-outcome':
      return { MP4: okrOutcomeVideoMP4, Webm: okrOutcomeVideoWebm }
    case 'okr-linking':
      return { MP4: okrLinkingVideoMP4, Webm: okrLinkingVideoWebm }
    case 'okr-progress':
      return { MP4: okrProgressVideoMP4, Webm: okrProgressVideoWebm }
    case 'okr-transparency':
      return { MP4: okrTransparencyVideoMP4, Webm: okrTransparencyVideoWebm }
    case 'okr-framework':
      return { MP4: okrFrameworkVideoMP4, Webm: okrFrameworkVideoWebm }
  }
}

export const getSortedEvents = (
  events: {
    edges: {
      node: Event
    }[]
  },
  withSort: boolean = true
) => {
  return events.edges
    .sort((a, b) => (withSort ? new Date(a.node.date).getTime() - new Date(b.node.date).getTime() : 0))
    .map((event) => {
      return {
        ...event.node,
      }
    })
}

export const getSingularForm = (name: string) => {
  return name.split('').slice(0, -1).join('')
}

export const calculateIfSecondPrevious = (currentIndex: number, index: number, arrayLength: number) =>
  currentIndex - index < 0 ? currentIndex - (index - arrayLength) === 2 : currentIndex - index === 2

export const calculateIfPrevious = (currentIndex: number, index: number, arrayLength: number) =>
  currentIndex - index < 0 ? currentIndex - (index - arrayLength) === 1 : currentIndex - index === 1

export const calculateIfNext = (currentIndex: number, index: number, arrayLength: number) =>
  index - currentIndex < 0 ? arrayLength + index - currentIndex === 1 : index - currentIndex === 1

export const calculateIfSecondNext = (currentIndex: number, index: number, arrayLength: number) =>
  index - currentIndex < 0 ? arrayLength + index - currentIndex === 2 : index - currentIndex === 2

export const useValueRef = <T>(value: T) => {
  const ref = React.useRef(value)
  ref.current = value
  return ref
}

const animateLottie = (lottieObj: LottieRefCurrentProps, animationProgress: number) => {
  if (lottieObj.animationItem) {
    const animationFrame = animationProgress * (lottieObj.animationItem as any).animationData.op
    lottieObj.goToAndStop(animationFrame, true)
  }
}

interface ScrollingLottieAnimationOptions {
  containerRef?: React.RefObject<HTMLElement>
  damping?: number
}

export const useScrollingLottieAnimation = (
  lottieObj: LottieRefCurrentProps,
  [from, until]: [number, number],
  options: ScrollingLottieAnimationOptions = {}
) => {
  const reference = useValueRef(options.containerRef ?? lottieObj.animationContainerRef)
  const lottieObjRef = useValueRef(lottieObj)
  const fromUntilRef = useValueRef<[number, number]>([from, until])
  React.useEffect(() => {
    let animationFrame: number | null = null

    const calculate = (referenceRef: React.RefObject<HTMLElement>) => {
      if (referenceRef.current) {
        const [from, until] = fromUntilRef.current
        const { top } = referenceRef.current.getBoundingClientRect()
        const progress = (window.innerHeight - top) / window.innerHeight
        return (progress - from) / (until - from)
      } else {
        return 0
      }
    }

    let dampedProgress = calculate(reference.current)
    const animate = () => {
      const transformedProgress = calculate(reference.current)
      dampedProgress = dampedProgress + (transformedProgress - dampedProgress) / (1 + (options.damping ?? 10))
      animateLottie(lottieObjRef.current, clamp(dampedProgress, 0, 1))
      animationFrame = requestAnimationFrame(animate)
    }
    animationFrame = requestAnimationFrame(animate)
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
      }
    }
  }, [])
}

const clamp = (number: number, min: number, max: number) => {
  return Math.min(Math.max(number, min), max)
}

export const getDateDayAppendix = (day: string) => {
  const dayInteger = parseInt(day)
  if (dayInteger >= 11 && dayInteger <= 13) {
    return 'th'
  }
  switch (dayInteger % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

// MOVED TO AuthorAvatar.tsx BECAUSE OF IMPORT OF THIS FILE (common.ts) BREAKS THE BUILD
// export const getAuthorAvatarPublicationsTerm = (locationPathname: string) => {
//   switch (true) {
//     case locationPathname.includes('blog'):
//     case locationPathname.includes('product-learn'):
//       return 'Article'
//     case locationPathname.includes('templates'):
//       return 'Template'
//     default:
//       return 'Publication'
//   }
// }

export const reloadIfCurrentPathMatchesRedirect = (event: React.MouseEvent<HTMLAnchorElement>, url: string) => {
  if (window.location.pathname.includes(url)) {
    // Refresh the page
    event.preventDefault()
    window.location.reload()
  }
}

export const getBackgroundColorFromIndex = (index: number, breakPoint: number = breakpoints.medium) => {
  switch (index % 6) {
    case 0:
      return 'blue'
    case 1:
      return 'peach'
    case 2:
      return isMobile(breakPoint) && !isMobile(breakpoints.small) ? 'purple' : 'blue'
    case 3:
      return isMobile(breakPoint) && !isMobile(breakpoints.small) ? 'blue' : 'purple'
    case 4:
      return 'blue'
    case 5:
      return 'green'
    default:
      return 'blue'
  }
}
